import { AsYouType, type CountryCode, parseDigits } from 'libphonenumber-js';

/**
 * Region a national number is read as. A value that starts with `+` carries its
 * own dialing code and is parsed as that country instead.
 */
const DEFAULT_TEL_COUNTRY: CountryCode = 'US';

export interface FormatTelInputOptions {
  /** Region a national number is read as. @default "US" */
  country?: CountryCode;
  /**
   * True when the keystroke removed characters. Deletions are returned as typed:
   * reformatting one re-adds the separator that was just removed, which reads as
   * a stuck backspace. The next inserted character formats again.
   */
  deleting?: boolean;
}

// libphonenumber's own digit parser, not an ASCII range: it accepts full-width
// and Arabic-Indic digits and folds them to ASCII. A caret anchored on a narrower
// definition of "digit" than the formatter uses lands in the wrong place, and a
// value that keeps non-ASCII digits is not a phone number anyone can store.
const isDigit = (character: string): boolean => parseDigits(character).length === 1;

/** Digits in `value`, counted the way the formatter counts them. */
export const countTelDigits = (value: string): number => parseDigits(value).length;

const isBareDigitRun = (value: string): boolean => parseDigits(value).length === value.length;

/**
 * Progressively formats phone input one keystroke at a time, the way a phone
 * field is expected to behave while it is being typed into.
 *
 * A number that is complete for `country` collapses to its national form, so a
 * stored E.164 value (`+14083707063`) displays as `(408) 370-7063`. Everything
 * else keeps the international grouping libphonenumber derives from the dialing
 * code, so non-US numbers are formatted rather than mangled.
 */
export function formatTelInput(value: string, options: FormatTelInputOptions = {}): string {
  const { country = DEFAULT_TEL_COUNTRY, deleting = false } = options;

  if (deleting) {
    return value;
  }

  const asYouType = new AsYouType(country);
  const formatted = asYouType.input(value);

  // Past the longest pattern it knows, libphonenumber stops templating and
  // returns a bare digit run. Keep the text the field already shows rather than
  // stripping the separators out from under the caret.
  if (isBareDigitRun(formatted) && !isBareDigitRun(value)) {
    return value;
  }

  const parsed = asYouType.getNumber();
  if (parsed?.country === country && asYouType.isValid()) {
    return parsed.formatNational();
  }
  return formatted;
}

/** Longest a number can be in E.164, so no real number is ever cut short. */
export const E164_MAX_DIGITS = 15;

// The characters the formatter itself emits. A filtered value keeps them so it
// stays shaped like the text on screen, which is what the caret and the deleting
// path both read; reducing input to a bare digit run instead would strip the
// separators out from under a number that has outgrown every pattern.
const TEL_SEPARATORS = new Set(['(', ')', '-', ' ']);

export interface RestrictTelInputOptions {
  /** Digits kept; the rest are dropped. @default 15 */
  maxDigits?: number;
}

/**
 * Input narrowed to what a phone field can hold, so a character the field cannot
 * use is refused outright rather than accepted and reported invalid afterwards.
 *
 * Digits are folded the way the formatter reads them, so a full-width or
 * Arabic-Indic digit is kept as its ASCII equivalent rather than thrown out as a
 * non-digit. A leading `+` survives, matching `unformatTel`.
 */
export function restrictTelInput(value: string, options: RestrictTelInputOptions = {}): string {
  const { maxDigits = E164_MAX_DIGITS } = options;

  // Whether the value holds a number at all, rather than how many digits precede
  // a given separator: the field's own text starts with `(`, so a separator has to
  // survive ahead of the first digit. What must not survive is punctuation with no
  // number anywhere to punctuate.
  const punctuates = countTelDigits(value) > 0;

  let digits = 0;
  let restricted = '';
  let international = false;
  for (const character of value) {
    const digit = parseDigits(character);
    if (digit.length === 1) {
      if (digits >= maxDigits) {
        continue;
      }
      digits += 1;
      restricted += digit;
      continue;
    }
    // Dropped here and re-added below, so a leading one is never doubled and an
    // interior one cannot survive. What makes it a dialing prefix is sitting
    // ahead of every digit rather than at index 0, which is libphonenumber's own
    // rule: `(+44)` marks an international number as surely as `+44` does.
    if (character === '+') {
      international ||= digits === 0;
      continue;
    }
    // `((()---` would otherwise be shown while the reported value stayed empty,
    // which is the accept-then-reject behaviour this exists to end.
    if (punctuates && TEL_SEPARATORS.has(character)) {
      restricted += character;
    }
  }

  return international ? `+${restricted}` : restricted;
}

/**
 * The number without any presentation: digits as typed, keeping a leading `+` so
 * an international value survives. This is what a phone field reports and what
 * callers should persist (normalized to E.164 at that boundary) — the separators
 * exist only on screen.
 */
export function unformatTel(value: string): string {
  const digits = parseDigits(value);
  return value.trimStart().startsWith('+') ? `+${digits}` : digits;
}

/**
 * Where the caret belongs in `formatted` given its position in `value`, anchored
 * on the number of digits typed ahead of it. Without this, inserting a digit
 * mid-number sends the caret to the end of the field.
 */
export function telCaretPosition(value: string, caret: number, formatted: string): number {
  const digitsBeforeCaret = countTelDigits(value.slice(0, caret));
  if (digitsBeforeCaret === 0) {
    return Math.min(caret, formatted.length);
  }

  let seen = 0;
  for (let index = 0; index < formatted.length; index += 1) {
    if (isDigit(formatted[index])) {
      seen += 1;
      if (seen === digitsBeforeCaret) {
        return index + 1;
      }
    }
  }
  return formatted.length;
}
